/**
 * VRG-734 — the website's taxonomy, as URL slugs: the 6 solutions (`/solutions/*`) and the 16
 * published industries (`/industries/*`). Hoisted here from `apps/api/src/features/seo/seo-taxonomy.ts`
 * (VRG-643), which now re-exports these, because a VERAfied episode is tagged with the same values
 * and the platform needs the labels too. One list, not a fifth copy (working-conventions §36).
 *
 * Source: vera-website `content/solutions.ts` SPECS and `content/industries.ts` CMS_ORDER
 * (origin/main bdeeebe, 2026-10-22). The labels are the site's own.
 */
export const WEBSITE_INDUSTRY_SLUGS = [
  'apparel',
  'art',
  'auction',
  'eyewear',
  'footwear',
  'handbags',
  'jewelry',
  'legal-documents',
  'memorabilia',
  'music',
  'perfume',
  'supplements',
  'ticketing',
  'trading-cards',
  'watches',
  'wines-and-spirits',
] as const;
export type WebsiteIndustrySlug = (typeof WEBSITE_INDUSTRY_SLUGS)[number];

export const WEBSITE_INDUSTRY_LABELS: Readonly<Record<WebsiteIndustrySlug, string>> = {
  apparel: 'Apparel',
  art: 'Art',
  auction: 'Auction',
  eyewear: 'Eyewear',
  footwear: 'Footwear',
  handbags: 'Handbags',
  jewelry: 'Jewelry',
  'legal-documents': 'Legal documents',
  memorabilia: 'Memorabilia',
  music: 'Music',
  perfume: 'Perfume',
  supplements: 'Supplements',
  ticketing: 'Ticketing',
  'trading-cards': 'Trading cards',
  watches: 'Watches',
  'wines-and-spirits': 'Wines & spirits',
};

export const WEBSITE_SOLUTION_SLUGS = [
  'brand-protection',
  'resale-circularity',
  'traceability-logistics',
  'consumer-engagement',
  'sustainability-compliance',
  'ai-analytics',
] as const;
export type WebsiteSolutionSlug = (typeof WEBSITE_SOLUTION_SLUGS)[number];

/** Verbatim from the site (and `METRIC_SOLUTION_LABELS`). */
export const WEBSITE_SOLUTION_LABELS: Readonly<Record<WebsiteSolutionSlug, string>> = {
  'brand-protection': 'Brand Protection',
  'resale-circularity': 'Resale & Circularity',
  'traceability-logistics': 'Traceability & Logistics',
  'consumer-engagement': 'Consumer Engagement',
  'sustainability-compliance': 'Sustainability & Compliance',
  'ai-analytics': 'AI & Analytics',
};

export const isWebsiteIndustrySlug = (v: unknown): v is WebsiteIndustrySlug =>
  typeof v === 'string' && (WEBSITE_INDUSTRY_SLUGS as readonly string[]).includes(v);
export const isWebsiteSolutionSlug = (v: unknown): v is WebsiteSolutionSlug =>
  typeof v === 'string' && (WEBSITE_SOLUTION_SLUGS as readonly string[]).includes(v);
